import type { Defect, Scenario } from '../game/types';
import type { Grade } from '../game/scoring';
import { falsePositivePenalty } from '../game/scoring';
import { SiteFooter } from './SiteFooter';
import { ScorePanel } from './ScorePanel';

interface Props {
  scenario: Scenario;
  result: Grade;
  onPlayAgain: () => void;
  onReviewBoard: () => void;
  /** Out of the case entirely. The other two only circle within it. */
  onExit: () => void;
}

export function Results({
  scenario,
  result,
  onPlayAgain,
  onReviewBoard,
  onExit,
}: Props) {
  const total = scenario.defects.length;

  return (
    <div className="panel">
      <div className="panel__inner">
        <ScorePanel
          score={result.score}
          rating={result.rating}
          found={result.found.length}
          total={total}
          missed={result.missed.length}
          falseAlarms={result.falsePositives.length}
        />

        <p className="lede">
          Score weights findings by severity, then cancels one correct find for
          every false alarm — about{' '}
          {Math.round(falsePositivePenalty(scenario.defects.length))} points each
          here. A reviewer who flags everything finds everything and is still no
          use.
        </p>

        {result.missed.length > 0 && (
          <>
            <h2>Missed — {result.missed.length}</h2>
            {result.missed.map((defect) => (
              <Finding key={defect.id} defect={defect} tone="missed" scenario={scenario} />
            ))}
          </>
        )}

        {result.found.length > 0 && (
          <>
            <h2>Found — {result.found.length}</h2>
            {result.found.map((defect) => (
              <Finding key={defect.id} defect={defect} tone="found" scenario={scenario} />
            ))}
          </>
        )}

        {result.falsePositives.length > 0 && (
          <>
            <h2>False alarms — {result.falsePositives.length}</h2>
            {result.falsePositives.map((key) => (
              <div key={key} className="finding finding--fp">
                <div className="finding__head">
                  <span className="finding__where">{describeTarget(scenario, key)}</span>
                  <span className="finding__title">Nothing wrong here</span>
                </div>
                <p className="finding__body">
                  You flagged this, but it is correctly configured. Over-flagging
                  is its own failure mode: it buries the real findings.
                </p>
              </div>
            ))}
          </>
        )}

        <div className="sheet__actions" style={{ marginTop: 28 }}>
          <button type="button" className="btn" onClick={onPlayAgain}>
            Play again
          </button>
          <button type="button" className="btn btn--ghost" onClick={onReviewBoard}>
            Back to the board
          </button>
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            All cases
          </button>
        </div>

        <SiteFooter />
      </div>
    </div>
  );
}

function Finding({
  defect,
  tone,
  scenario,
}: {
  defect: Defect;
  tone: 'found' | 'missed';
  scenario: Scenario;
}) {
  return (
    <div className={`finding finding--${tone}`}>
      <div className="finding__head">
        <span className="finding__where">
          {describeTarget(scenario, `${defect.target.kind}:${defect.target.id}`)}
        </span>
        <span className={`sev sev--${defect.severity}`}>{defect.severity}</span>
        <span className="finding__title">{defect.title}</span>
      </div>
      <p className="finding__body">{defect.explanation}</p>
      <p className="finding__fix">
        <b>Fix:</b> {defect.fix}
      </p>
    </div>
  );
}

/** Turn a raw target key such as "column:inprogress" into something readable. */
export function describeTarget(scenario: Scenario, key: string): string {
  const separator = key.indexOf(':');
  const kind = key.slice(0, separator);
  const id = key.slice(separator + 1);

  if (kind === 'issue') return id;
  if (kind === 'sprint') return id;
  if (kind === 'column') {
    const column = scenario.columns.find((c) => c.id === id);
    return column ? `${column.name} column` : `${id} column`;
  }
  return key;
}
